import { toast } from 'sonner'

import { useCreateSuggestion, usePatchSingleField } from '@/api/queries'
import type { FieldConflictPayload, PatchSingleFieldBody } from '@/api/docfiller-types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

export interface FieldConflictDialogProps {
  documentId: string
  /** Conflict to surface — null hides the dialog. */
  conflict: FieldConflictPayload | null
  /** Human-friendly label for the conflicting field. */
  fieldLabel: string
  /**
   * The original PATCH body that triggered the conflict. When provided,
   * "Keep mine" merges these fields (e.g. ``accepted_by``, ``clear_suggested``)
   * into the retry payload so agent-suggestion accepts are replayed correctly
   * rather than being stripped to a bare ``current_value`` write.
   * ``base_revision`` in this body is always overridden with the server's
   * current revision from the conflict payload.
   */
  originalBody?: PatchSingleFieldBody
  onResolved: () => void
  onDismiss: () => void
}

/**
 * Surfaces a field-revision conflict so the user can decide how to act on
 * the optimistic-concurrency rejection. Three options:
 *
 * - "Keep mine" — retry the PATCH with the server's latest revision so the
 *   attempted value lands as the new current_value (last-write-wins, but
 *   only after we showed the user the other writer's value).
 * - "Accept theirs" — drop the attempted value, close the dialog, and let
 *   the realtime cache update stand.
 * - "Save as suggestion" — record the attempted value as a comment-kind
 *   suggestion. Lets the other user review it without clobbering.
 */
export function FieldConflictDialog({
  documentId,
  conflict,
  fieldLabel,
  originalBody,
  onResolved,
  onDismiss,
}: FieldConflictDialogProps) {
  const patch = usePatchSingleField(documentId)
  const createSuggestion = useCreateSuggestion(documentId)
  const anyBusy = patch.isPending || createSuggestion.isPending

  if (!conflict) return null

  const keepMine = async () => {
    if (anyBusy) return
    try {
      await patch.mutateAsync({
        fieldId: conflict.field_uuid,
        body: {
          // Replay the original body (preserves accepted_by, clear_suggested,
          // suggested_value etc.) but override current_value with the
          // conflict-confirmed attempted value and advance base_revision to
          // the server's current revision so the retry lands cleanly.
          ...(originalBody ?? {}),
          current_value: conflict.attempted_value ?? null,
          base_revision: conflict.current_revision,
        },
      })
      toast.success(`Kept your value for "${fieldLabel}"`)
      onResolved()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Retry failed')
    }
  }

  const acceptTheirs = () => {
    if (anyBusy) return
    onDismiss()
  }

  const saveAsSuggestion = async () => {
    if (anyBusy) return
    try {
      await createSuggestion.mutateAsync({
        field_uuid: conflict.field_uuid,
        proposed_value: conflict.attempted_value ?? null,
        note: `Proposed value (conflict on rev ${conflict.current_revision})`,
      })
      toast.success(`Saved as a suggestion for "${fieldLabel}"`)
      onResolved()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save suggestion failed')
    }
  }

  return (
    <Dialog
      open={true}
      onOpenChange={(next) => {
        if (!next) onDismiss()
      }}
    >
      <DialogContent data-testid="field-conflict-dialog">
        <DialogHeader>
          <DialogTitle>Edit conflict for "{fieldLabel}"</DialogTitle>
          <DialogDescription>
            Another collaborator saved a change before yours landed. Choose
            which value should win.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <ComparisonRow
            label="Their current value"
            tone="info"
            value={conflict.current_value}
          />
          <ComparisonRow
            label="Your attempted value"
            tone="warning"
            value={conflict.attempted_value ?? null}
          />
          <p className="text-xs text-muted-foreground">
            Server revision is now {conflict.current_revision}. You started
            from revision {conflict.base_revision}.
          </p>
        </div>

        <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button
            variant="outline"
            onClick={acceptTheirs}
            disabled={anyBusy}
            data-testid="conflict-accept-theirs"
          >
            Accept theirs
          </Button>
          <Button
            variant="soft"
            onClick={saveAsSuggestion}
            disabled={anyBusy}
            data-testid="conflict-save-suggestion"
          >
            Save as suggestion
          </Button>
          <Button
            onClick={keepMine}
            disabled={anyBusy}
            data-testid="conflict-keep-mine"
          >
            Keep mine
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ComparisonRow({
  label,
  value,
  tone,
}: {
  label: string
  value: string | null | undefined
  tone: 'info' | 'warning'
}) {
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        <Badge size="xs" tone={tone}>
          rev
        </Badge>
      </div>
      <div className="mt-1 font-mono text-sm text-foreground">
        {value && value.length > 0 ? value : '(empty)'}
      </div>
    </div>
  )
}
